import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import { Funding } from '../../../../stores/tunnel/model/funding';
import { TunnelDataStore } from '../../../../stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from '../../../../stores/tunnel/tunnel-navigation.store';
import { KitchenService } from '../../../../services/configurators-2d/applications/kitchen.service';
import { LiteOfferResult } from '../../../../models/configurators-2d/tunnel/kitchen/kitchen-tunnel-store-model';
import { DiscountTypeEnum } from '../../../../enum/discount-type.enum';
import { StatisticService } from 'src/app/services/statistic.service';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { KitchenConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/kitchen-configuration-data';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, shareReplay, skip } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';
import { isFundingEmpty, isFundingStateEmpty } from '../../../../services/helpers/funding.helper';

type Column = {
  header: string;
  field: string;
};
export const DEFAULT_DURATION: number = 10;
export const DEFAULT_DURATION_MARGIN: number = 15;

@Component({
  selector: 'ng-casto-kitchen-funding',
  templateUrl: './kitchen-funding.component.html',
})
export class KitchenFundingComponent extends BaseTunnelObject implements OnInit, OnDestroy {
  //injects
  protected tunnelDataStore = inject(TunnelDataStore);
  protected tunnelNavigationStore = inject(TunnelNavigationStore);
  protected kitchenService = inject(KitchenService);
  private readonly statisticService = inject(StatisticService);
  private destroyRef = inject(DestroyRef);
  protected featureSettingsService = inject(FeatureSettingsService);

  //observables de base
  protected currentPricing$ = this.tunnelDataStore.getCurrentPricing();
  protected currentFunding$ = this.tunnelDataStore.getCurrentFunding();
  protected currentDiscount$ = this.tunnelDataStore.getCurrentDiscount();
  protected currentWithInstallation$ = this.tunnelDataStore.getCurrentWithInstallation();
  protected pricingLoading$ = this.tunnelDataStore.getPricingLoading();

  protected kitchenConfiguration$ = this.tunnelDataStore.getConfigurationData().pipe(
    map(configurationData => configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData),
    shareReplay(1)
  );

  //observables dérivés
  protected discountNumberValue$ = this.currentDiscount$.pipe(
    map(discount => {
      switch (discount) {
        case DiscountTypeEnum.Discount0:
          return 0;
        case DiscountTypeEnum.Discount5:
          return 5;
        case DiscountTypeEnum.Discount10:
          return 10;
        case DiscountTypeEnum.Discount20:
          return 20;
        default:
          return 0;
      }
    }),
    shareReplay(1)
  );

  protected displayDiscount$ = this.discountNumberValue$.pipe(map(value => value > 0));

  protected funding$ = combineLatest([this.currentFunding$, this.currentWithInstallation$]).pipe(
    map(([funding, withInstallation]) => {
      if (!funding) return null;
      return funding[withInstallation ? 'fundingWithInstallation' : 'fundingWithoutInstallation'];
    }),
    shareReplay(1)
  );

  protected offers$ = this.funding$.pipe(map(funding => funding?.offerTable ?? []));

  protected offersWithFooter$ = this.offers$.pipe(
    map(offers => (offers.length > 0 ? [...offers, { isTextContainer: true }] : []))
  );

  protected downPayment$ = new BehaviorSubject<number>(0);

  get downPaymentValue(): number {
    return this.downPayment$.getValue();
  }

  set downPaymentValue(value: number) {
    this.downPayment$.next(value);
  }

  protected fundingAmount$ = combineLatest([this.currentPricing$, this.downPayment$]).pipe(
    map(([pricing, downPayment]) => Math.max((pricing?.totalWithoutFunding.actualAmount ?? 0) - (downPayment ?? 0), 0))
  );

  protected isNoOffer$ = this.funding$.pipe(map(funding => isFundingEmpty(funding)));

  protected fundingViewModel$ = combineLatest({
    pricingLoading: this.pricingLoading$,
    downPayment: this.downPayment$,
    currentPricing: this.currentPricing$,
    displayDiscount: this.displayDiscount$,
    fundingAmount: this.fundingAmount$,
    isNoOffer: this.isNoOffer$,
    offers: this.offers$,
    offersWithFooter: this.offersWithFooter$,
    funding: this.funding$,
    currentWithInstallation: this.currentWithInstallation$,
  });

  // Propriétés locales
  protected setWithInstallationLoading = false;
  protected selectOfferLoading = false;
  protected updateFundingLoading = false;

  get loading() {
    return this.setWithInstallationLoading || this.selectOfferLoading || this.updateFundingLoading;
  }

  //Constantes

  protected readonly offersColumns: Column[] = [
    { header: 'Barème', field: 'code' },
    { header: 'Offre', field: 'offer' },
    { header: 'Mensualité', field: 'mensuality' },
    { header: 'Durée', field: 'duration' },
    { header: 'Total intérêts', field: 'interest' },
    { header: 'Total dû', field: 'total' },
    { header: 'TAEG', field: 'taeg' },
  ];

  ngOnInit(): void {
    super.ngOnInit();
    void this.initFundingStep();
  }

  private async initFundingStep(): Promise<void> {
    await this.initFundingIfNeeded();
    await this.sendAccessFundingStatistic();
  }

  async sendAccessFundingStatistic() {
    const state = this.tunnelDataStore.getCurrentStateValue();
    const kitchenConfig = state.configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;

    this.statisticService.sendStatistic(
      StatisticTypeEnum.FUNDING_COUNT,
      1,
      CONFIG_CONTEXT.KITCHEN,
      kitchenConfig?.configurationId
    );
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  protected subscribeToObservable() {
    this.currentWithInstallation$
      .pipe(
        filter(value => value !== undefined),
        distinctUntilChanged(), // Ignore les valeurs identiques
        skip(1), // Ignore la première émission
        filter(() => !this.loading), // Ignore si loading
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.updateFunding();
      });

    // Initialise downPayment depuis le funding
    this.currentFunding$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(funding => funding?.downPayment ?? 0)
      )
      .subscribe(downPayment => {
        // Ne met à jour que si c'est la première valeur
        if (this.downPayment$.getValue() === 0) {
          this.downPayment$.next(downPayment);
        }
      });
  }

  private async initFundingIfNeeded(): Promise<void> {
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

    if (!financementEnabled) {
      await this.ensurePricingIfNeeded();
      this.tunnelDataStore.disableCurrentFunding();
      return;
    }

    if (isFundingStateEmpty(this.tunnelDataStore.getCurrentStateValue().currentFunding)) {
      await this.updateFunding();
    }
  }

  private async ensurePricingIfNeeded(): Promise<void> {
    const currentPricing = this.tunnelDataStore.getCurrentStateValue().currentPricing;
    if (!currentPricing?.totalWithoutFunding) {
      await this.kitchenService.computePricing();
    }
  }

  protected async selectOffer(offer: LiteOfferResult): Promise<void> {
    if (!offer) return;

    try {
      this.selectOfferLoading = true;
      await this.kitchenService.selectOffer(offer.code, offer.duration);
    } catch (error) {
      console.error("Erreur lors de la sélection de l'offre:", error);
    } finally {
      this.selectOfferLoading = false;
    }
  }

  protected isOfferSelected(offer: LiteOfferResult, funding: Funding): boolean {
    if (!offer || !funding) {
      return false;
    }

    return (
      funding.mensuality === offer.mensuality && funding.duration === offer.duration && funding.code === offer.code
    );
  }

  protected async setWithInstallation(withInstallation: boolean): Promise<void> {
    try {
      this.setWithInstallationLoading = true;
      await this.kitchenService.updateWithInstallation(withInstallation);
    } catch (error) {
      console.error('Erreur lors de la mise à jour de withInstallation:', error);
    } finally {
      this.setWithInstallationLoading = false;
    }
    this.tunnelDataStore.setWithInstallation(withInstallation);
  }

  protected updateDownPayment(value: number): void {
    this.downPayment$.next(value);
  }

  private checkDownPayment(): void {
    const pricing = this.tunnelDataStore.getCurrentStateValue().currentPricing;
    const currentDownPayment = this.downPayment$.getValue();

    if (currentDownPayment > pricing?.totalWithoutFunding.actualAmount) {
      this.downPayment$.next(pricing.totalWithoutFunding.actualAmount);
    }
  }

  private async computeFunding(): Promise<void> {
    const state = this.tunnelDataStore.getCurrentStateValue();
    const kitchenConfig = state.configurationData.get(CONFIG_CONTEXT.KITCHEN) as KitchenConfigurationData;

    await this.kitchenService.computeFunding({
      tunnelConfigurationId: kitchenConfig?.configurationId,
      downPaymentAmount: this.downPayment$.getValue(),
      shouldRecalculateFinancing: true,
    });
  }

  protected async updateFunding(): Promise<void> {
    try {
      this.updateFundingLoading = true;
      const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

      if (!financementEnabled) {
        await this.ensurePricingIfNeeded();
        this.tunnelDataStore.disableCurrentFunding();
        this.checkDownPayment();
        return;
      }

      this.checkDownPayment();
      await this.computeFunding();
    } catch (error) {
      console.error('Erreur lors de la mise à jour du financement:', error);
    } finally {
      this.updateFundingLoading = false;
    }
  }

  protected createSkeletonRow(): number[] {
    return Array(4).fill(0);
  }
}
